import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  getAllEntries, getOfficialWinners, saveOfficialWinners, getSettings, saveSettings, syncDB 
} from '../utils/db';
import { 
  playRollSound, playWinFanfare, playTickSound, playJackpotSound 
} from '../utils/soundEffects';
import ConfettiEffect from './ConfettiEffect';

const { 
  FiAward, FiPlay, FiRefreshCw, FiExternalLink, FiMaximize2, 
  FiMinimize2, FiCheck, FiX, FiFilter, FiDownload, FiStar,
  FiVolume2, FiSliders, FiUsers, FiTag, FiRadio, FiEye, FiEyeOff,
  FiFastForward
} = FiIcons;

// Colors for bouncy token balls inside the glass pot
// Colors for bouncy token balls inside the glass pot
const BALL_COLORS = [
  'from-amber-400 to-amber-600',
  'from-blue-400 to-indigo-600',
  'from-emerald-400 to-teal-600',
  'from-purple-400 to-pink-600',
  'from-rose-400 to-red-600',
  'from-cyan-400 to-blue-600',
  'from-yellow-300 to-orange-500'
];

const BROADCAST_STORAGE_KEY = 'gamifycenter_pot_broadcast';

// Recognized Event Game types from kiosk / booth stations
export const EVENT_GAME_TYPES = [
  'Scratch Card',
  'Scratch & Win',
  'Spin Wheel',
  'Spin the Wheel',
  'Mega Spin Wheel',
  'Peel & Reveal',
  'Slot Machine',
  'Flip Card',
  'Mystery Flip Card',
  'Balloon Pop',
  'Balloon Pop Carnival',
  'Magic Treasure Box',
  'Treasure Box',
  'Candy Piñata',
  'Candy Piñata Bash',
  'Animal Safari Match',
  'Rainbow Bubble Pop',
  'Astro-Engineer Rocket',
  'Cosmic Gravity Lab',
  'Magic Color Lab'
];

// Helper to determine if an entry belongs to school games, quizzes, barometers, or sports matches
export const isSchoolGameEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return false;
  const lowerGame = (entry.gameType || '').toLowerCase();
  const lowerCat = (entry.category || '').toLowerCase();
  const lowerName = (entry.name || '').toLowerCase();
  const lowerNotes = (entry.notes || '').toLowerCase();

  return (
    lowerGame.includes('school') ||
    lowerGame.includes('quiz') ||
    lowerGame.includes('barometer') ||
    lowerGame.includes('champion') ||
    lowerGame.includes('sports') ||
    lowerGame.includes('house') ||
    lowerGame.includes('emoji quest') ||
    lowerGame.includes('word scramble') ||
    lowerCat.includes('school') ||
    lowerCat.includes('quiz') ||
    lowerCat.includes('sports') ||
    lowerCat.includes('barometer') ||
    lowerNotes.includes('inter-house') ||
    lowerNotes.includes('sports day') ||
    Boolean(entry.houseId) ||
    Boolean(entry.athlete)
  );
};

// Helper to verify if an entry is genuinely an Event Game participant
export const isEventGameParticipantEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return false;
  if (isSchoolGameEntry(entry)) return false;

  const lowerGame = (entry.gameType || '').trim().toLowerCase();
  const matchesKnownEventGame = EVENT_GAME_TYPES.some(type => 
    lowerGame === type.toLowerCase() || lowerGame.includes(type.toLowerCase())
  );

  const hasGameCoupon = Boolean(entry.couponCode || entry.token || entry.serialNumber);
  const isGenericEventGame = 
    lowerGame.includes('spin') || 
    lowerGame.includes('scratch') || 
    lowerGame.includes('slot') || 
    lowerGame.includes('peel') || 
    lowerGame.includes('balloon') || 
    lowerGame.includes('treasure') || 
    lowerGame.includes('pinata') || 
    lowerGame.includes('piñata');

  return matchesKnownEventGame || hasGameCoupon || isGenericEventGame;
};

// Helper to check if an entry is in the Announced Winners Directory
export const isAnnouncedWinnerEntry = (entry, officialWinnersList = []) => {
  if (!entry || !Array.isArray(officialWinnersList) || officialWinnersList.length === 0) return false;

  const entryToken = (entry.token || entry.serialNumber || entry.couponCode || '').trim().toLowerCase();
  const entryId = String(entry.id || '').trim().toLowerCase();

  return officialWinnersList.some(winner => {
    // 1. Direct Entry ID or Announced ID check
    if (winner.id && entryId && String(winner.id).toLowerCase() === entryId) return true;
    if (winner.announcedId && entryId && String(winner.announcedId).toLowerCase() === entryId) return true;

    // 2. Token / Serial Number / Coupon Code check
    const winToken = (winner.token || winner.serialNumber || winner.couponCode || '').trim().toLowerCase();
    if (winToken && entryToken && winToken === entryToken) return true;

    return false;
  });
};

const loadSavedPotState = () => {
  try {
    const raw = localStorage.getItem(BROADCAST_STORAGE_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      const totalDraws = [1, 2, 3].includes(data.totalDraws) ? data.totalDraws : 3;
      return {
        totalDraws,
        currentStep: data.currentStep !== undefined ? data.currentStep : totalDraws,
        currentWinner: data.currentWinner || null,
        drawnWinners: data.drawnWinners || { third: null, second: null, first: null },
        drawStatus: data.drawStatus === 'drawing' ? 'revealed' : (data.drawStatus || 'idle')
      };
    }
  } catch (e) {}
  return {
    totalDraws: 3,
    currentStep: 3,
    currentWinner: null,
    drawnWinners: { third: null, second: null, first: null },
    drawStatus: 'idle'
  };
};

const GlassPotTokenDraw = ({ isAdmin = false, isStageMode = false, onClose = null }) => {
  const [entries, setEntries] = useState(getAllEntries());
  const [settings, setSettings] = useState(getSettings());
  const [officialWinners, setOfficialWinners] = useState(getOfficialWinners());
  
  // Persisted Draw State: restores current step and drawn podium winners across page navigation
  const initialPotState = loadSavedPotState();
  const [totalDraws, setTotalDraws] = useState(initialPotState.totalDraws || 3);
  const [drawStatus, setDrawStatus] = useState(initialPotState.drawStatus);
  const [currentStep, setCurrentStep] = useState(initialPotState.currentStep);
  const [currentWinner, setCurrentWinner] = useState(initialPotState.currentWinner);
  const [drawnWinners, setDrawnWinners] = useState(initialPotState.drawnWinners);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [potAgitating, setPotAgitating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshFeedback, setRefreshFeedback] = useState(null);
  // Filter ONLY genuine Event Game Participants (strictly excluding school games)
  // By default, all entered event participants are eligible so the draw is never blocked unexpectedly.
  // Historical announced winners can optionally be excluded via the admin toggle.
  const [excludeAnnouncedWinners, setExcludeAnnouncedWinners] = useState(false);

  const allEventParticipants = (entries || []).filter(isEventGameParticipantEntry);
  const eligibleEventParticipants = excludeAnnouncedWinners
    ? allEventParticipants.filter(e => !isAnnouncedWinnerEntry(e, officialWinners))
    : allEventParticipants;
  const excludedAnnouncedWinnersCount = allEventParticipants.filter(e => isAnnouncedWinnerEntry(e, officialWinners)).length;

  // Token balls simulation state inside glass pot
  const [balls, setBalls] = useState([]);

  // Generate tokens strictly from eligible Event Game participants
  useEffect(() => {
    let tokenPool = eligibleEventParticipants.map((e, idx) => ({
      id: e.id || `ent_${idx}`,
      token: e.serialNumber || e.token || e.couponCode || `TKN-${Math.floor(1000 + (idx * 137) % 8999)}`,
      name: e.name || `Player #${idx + 1}`,
      mobile: e.mobile || '—',
      gameType: e.gameType || 'Event Game Entry',
      timestamp: e.timestamp || e.createdAt || new Date().toISOString(),
      isEligible: true
    }));

    // If there are real eligible participants but fewer than 24, duplicate their balls visually
    // so that the pot looks full and lively without inventing fake members
    if (tokenPool.length > 0 && tokenPool.length < 24) {
      const needed = 24 - tokenPool.length;
      const realTokens = [...tokenPool];
      for (let i = 0; i < needed; i++) {
        const source = realTokens[i % realTokens.length];
        tokenPool.push({
          ...source,
          id: `vis_${source.id}_${i}`,
          isVisualDuplicate: true
        });
      }
    } else if (tokenPool.length === 0) {
      // If 0 eligible participants are currently in the pool, show ambient demo balls for visualization
      const placeholderTokens = ['TKN-101', 'TKN-202', 'TKN-303', 'TKN-404', 'TKN-505', 'TKN-606', 'TKN-707', 'TKN-808'];
      for (let i = 0; i < 20; i++) {
        tokenPool.push({
          id: `empty_pot_${i}`,
          token: placeholderTokens[i % placeholderTokens.length],
          name: 'Waiting for Event Players',
          mobile: '—',
          gameType: 'Awaiting Event Game Entry',
          timestamp: new Date().toISOString(),
          isPlaceholderOnly: true
        });
      }
    }

    const simulatedBalls = tokenPool.slice(0, 32).map((item, index) => ({
      ...item,
      color: BALL_COLORS[index % BALL_COLORS.length],
      x: 20 + Math.random() * 60,
      y: 35 + Math.random() * 50,
      size: 32 + (index % 3) * 6,
      vx: (Math.random() - 0.5) * 1.8,
      vy: (Math.random() - 0.5) * 1.8,
      rotation: Math.random() * 360
    }));

    setBalls(simulatedBalls);
  }, [entries, officialWinners, excludeAnnouncedWinners]);

  // Sync settings and broadcast state across tabs and navigation
  useEffect(() => {
    const handleStorage = () => {
      setSettings(getSettings());
      setOfficialWinners(getOfficialWinners());
      setEntries(getAllEntries());

      try {
        const raw = localStorage.getItem(BROADCAST_STORAGE_KEY);
        if (raw) {
          const data = JSON.parse(raw);
          if (data.isAgitating) {
            setPotAgitating(true);
            setDrawStatus('drawing');
            playRollSound(3200);
          } else {
            setPotAgitating(false);
          }
          if (data.totalDraws !== undefined) setTotalDraws(data.totalDraws);
          if (data.currentStep !== undefined) setCurrentStep(data.currentStep);
          if (data.currentWinner !== undefined) setCurrentWinner(data.currentWinner);
          if (data.drawnWinners) setDrawnWinners(data.drawnWinners);
          if (data.drawStatus && data.drawStatus !== 'drawing') setDrawStatus(data.drawStatus);
        }
      } catch (e) {}
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('settingsUpdated', handleStorage);
    window.addEventListener('potDrawBroadcastUpdated', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('settingsUpdated', handleStorage);
      window.removeEventListener('potDrawBroadcastUpdated', handleStorage);
    };
  }, []);

  // Physics animation tick for token balls inside the transparent glass bowl
  useEffect(() => {
    let animId;
    const updatePhysics = () => {
      setBalls(prevBalls => {
        return prevBalls.map(ball => {
          const speedMultiplier = potAgitating ? 5.5 : 0.8;
          let newX = ball.x + ball.vx * speedMultiplier;
          let newY = ball.y + ball.vy * speedMultiplier;
          let newVx = ball.vx;
          let newVy = ball.vy;

          if (newX < 14) { newX = 14; newVx = Math.abs(newVx); }
          if (newX > 86) { newX = 86; newVx = -Math.abs(newVx); }
          if (newY < 25) { newY = 25; newVy = Math.abs(newVy); }
          if (newY > 88) { newY = 88; newVy = -Math.abs(newVy); }

          return {
            ...ball,
            x: newX,
            y: newY,
            vx: newVx,
            vy: newVy,
            rotation: (ball.rotation + (potAgitating ? 12 : 1)) % 360
          };
        });
      });
      animId = requestAnimationFrame(updatePhysics);
    };

    animId = requestAnimationFrame(updatePhysics);
    return () => cancelAnimationFrame(animId);
  }, [potAgitating]);

  // Broadcast state to public visitors
  const broadcastState = (payload) => {
    try {
      localStorage.setItem(BROADCAST_STORAGE_KEY, JSON.stringify(payload));
      window.dispatchEvent(new Event('potDrawBroadcastUpdated'));
    } catch (e) {}
  };

  // Execute the Draw in Reverse Order: 3rd -> 2nd -> 1st (ADMIN ONLY)
  const handleInitiateDraw = (place) => {
    if (!isAdmin || drawStatus === 'drawing') return;

    // Check drawn winners in current session
    const drawnIds = [drawnWinners.third?.id, drawnWinners.second?.id, drawnWinners.first?.id].filter(Boolean);
    const drawnTokens = [
      drawnWinners.third?.token, 
      drawnWinners.second?.token, 
      drawnWinners.first?.token
    ].filter(Boolean);

    // Active pool: strictly drawn from eligible Event Game participants (excluding current ceremony winners)
    const activePool = eligibleEventParticipants.filter(p => {
      const pToken = p.serialNumber || p.token || p.couponCode;
      const isAlreadyDrawnId = drawnIds.includes(p.id);
      const isAlreadyDrawnToken = pToken && drawnTokens.includes(pToken);
      const isAnnounced = excludeAnnouncedWinners && isAnnouncedWinnerEntry(p, officialWinners);
      return !isAlreadyDrawnId && !isAlreadyDrawnToken && !isAnnounced;
    });

    if (activePool.length === 0) {
      alert(
        "Cannot initiate draw: No eligible Event Game participants available!\n\n" +
        "• All event participants have either already won in this ceremony or are already listed in the Announced Winners Directory.\n" +
        "• Participants from school games (Quizzes, Sports Barometers, Inter-House Champions) are strictly excluded from this draw.\n\n" +
        "Visitors must play an Event Game (Scratch, Spin, Slot, Peel, etc.) to participate in the Glass Pot Token Draw."
      );
      return;
    }

    setDrawStatus('drawing');
    setPotAgitating(true);
    playRollSound(3200);

    broadcastState({
      isAgitating: true,
      drawStatus: 'drawing',
      currentStep: place,
      drawnWinners,
      totalDraws,
      timestamp: Date.now()
    });

    // Randomly select strictly from eligible Event Game participants!
    const winningParticipant = activePool[Math.floor(Math.random() * activePool.length)];
    const winningToken = winningParticipant.serialNumber || winningParticipant.token || winningParticipant.couponCode || `TKN-${Math.floor(1000 + Math.random() * 9000)}`;

    setTimeout(() => {
      setPotAgitating(false);
      setDrawStatus('revealed');

      let prizeName = '';
      let placeLabel = '';
      let nextStep = place;
      let nextDrawn = { ...drawnWinners };

      if (place === 3) {
        placeLabel = '🥉 3rd Place Bronze Winner';
        prizeName = settings.megaDrawPrizes?.[2] || '3rd Prize - Exclusive Hamper';
        nextDrawn.third = { 
          ...winningParticipant, 
          token: winningToken,
          prizeName, 
          placeLabel 
        };
        nextStep = 2;
        playWinFanfare();
      } else if (place === 2) {
        placeLabel = '🥈 2nd Place Silver Winner';
        prizeName = settings.megaDrawPrizes?.[1] || '2nd Prize - Premium Gift';
        nextDrawn.second = { 
          ...winningParticipant, 
          token: winningToken,
          prizeName, 
          placeLabel 
        };
        nextStep = 1;
        playWinFanfare();
      } else if (place === 1) {
        placeLabel = '🥇 1st Place Grand Champion';
        prizeName = settings.megaDrawPrizes?.[0] || '1st Prize - Mega Bumper Grand Prize';
        nextDrawn.first = { 
          ...winningParticipant, 
          token: winningToken,
          prizeName, 
          placeLabel 
        };
        nextStep = 'completed';
        playJackpotSound();
      }

      setDrawnWinners(nextDrawn);
      setCurrentStep(nextStep);

      const winnerData = {
        id: winningParticipant.id || `win_${Date.now()}`,
        token: winningToken,
        serialNumber: winningParticipant.serialNumber || winningToken,
        couponCode: winningParticipant.couponCode || winningToken,
        name: winningParticipant.name,
        mobile: winningParticipant.mobile,
        gameType: winningParticipant.gameType || 'Event Game Entry',
        place,
        placeLabel,
        reward: prizeName,
        announcedAt: new Date().toISOString()
      };

      setCurrentWinner(winnerData);
      saveOfficialWinners([winnerData]);

      broadcastState({
        isAgitating: false,
        drawStatus: 'revealed',
        currentStep: nextStep,
        currentWinner: winnerData,
        drawnWinners: nextDrawn,
        totalDraws,
        timestamp: Date.now()
      });
    }, 3200);
  };

  // Manually refresh and check for newly entered participants
  const handleRefreshPool = async () => {
    setIsRefreshing(true);
    setRefreshFeedback(null);
    let freshEntries = null;
    let freshWinners = null;
    let freshSettings = null;

    try {
      if (typeof syncDB === 'function') {
        const synced = await syncDB(true);
        if (synced && Array.isArray(synced.entries)) {
          freshEntries = synced.entries;
          freshWinners = synced.officialWinners || getOfficialWinners();
          freshSettings = synced.settings || getSettings();
        }
      }
    } catch (e) {
      console.warn('Sync error:', e);
    }

    if (!freshEntries) {
      freshEntries = getAllEntries();
      freshWinners = getOfficialWinners();
      freshSettings = getSettings();
    }

    setEntries(freshEntries);
    setOfficialWinners(freshWinners);
    setSettings(freshSettings);

    const freshEligible = (freshEntries || [])
      .filter(isEventGameParticipantEntry)
      .filter(e => !excludeAnnouncedWinners || !isAnnouncedWinnerEntry(e, freshWinners));

    const drawnIds = [drawnWinners.third?.id, drawnWinners.second?.id, drawnWinners.first?.id].filter(Boolean);
    const drawnTokens = [drawnWinners.third?.token, drawnWinners.second?.token, drawnWinners.first?.token].filter(Boolean);
    const remainingCount = freshEligible.filter(p => {
      const pToken = p.serialNumber || p.token || p.couponCode;
      return !drawnIds.includes(p.id) && !drawnTokens.includes(pToken);
    }).length;

    setIsRefreshing(false);
    setRefreshFeedback(`Synced! ${remainingCount} eligible participant${remainingCount === 1 ? '' : 's'} available.`);
    playTickSound(750, 0.05);

    setTimeout(() => {
      setRefreshFeedback(null);
    }, 4500);
  };

  // Change ceremony format: 1 Draw (Grand), 2 Draws (2nd & 1st), or 3 Draws (Full Podium)
  const handleSelectTotalDraws = (count) => {
    if (drawStatus === 'drawing') return;
    setTotalDraws(count);
    
    // If no winners drawn yet in this round, adjust current starting step
    const noWinnersDrawnYet = !drawnWinners.third && !drawnWinners.second && !drawnWinners.first;
    let nextStep = currentStep;
    if (noWinnersDrawnYet) {
      nextStep = count; // 1 -> step 1; 2 -> step 2; 3 -> step 3
      setCurrentStep(nextStep);
    }

    if (isAdmin) {
      broadcastState({
        isAgitating: false,
        drawStatus: 'idle',
        currentStep: nextStep,
        currentWinner,
        drawnWinners,
        totalDraws: count,
        timestamp: Date.now()
      });
    }
    playTickSound(880, 0.04);
  };

  // Skip step to target step (e.g. skip directly to 1st place if participants are limited)
  const handleSkipToStep = (targetStep) => {
    if (drawStatus === 'drawing') return;
    setCurrentStep(targetStep);
    if (isAdmin) {
      broadcastState({
        isAgitating: false,
        drawStatus: 'idle',
        currentStep: targetStep,
        currentWinner,
        drawnWinners,
        totalDraws,
        timestamp: Date.now()
      });
    }
    playTickSound(880, 0.05);
  };

  // Conclude the draw ceremony early with currently drawn winners
  const handleConcludeEarly = () => {
    if (window.confirm("Conclude the draw ceremony now with the currently drawn winners?")) {
      setCurrentStep('completed');
      setDrawStatus('completed');
      playJackpotSound();
      if (isAdmin) {
        broadcastState({
          isAgitating: false,
          drawStatus: 'completed',
          currentStep: 'completed',
          currentWinner,
          drawnWinners,
          totalDraws,
          timestamp: Date.now()
        });
      }
    }
  };

  const handleResetDrawSession = () => {
    if (window.confirm("Start a new token draw ceremony? Previous winners remain recorded in the official winners list.")) {
      const resetWinners = { third: null, second: null, first: null };
      setDrawnWinners(resetWinners);
      const startStep = totalDraws === 1 ? 1 : totalDraws === 2 ? 2 : 3;
      setCurrentStep(startStep);
      setCurrentWinner(null);
      setDrawStatus('idle');
      playTickSound(600, 0.05);

      if (isAdmin) {
        broadcastState({
          isAgitating: false,
          drawStatus: 'idle',
          currentStep: startStep,
          currentWinner: null,
          drawnWinners: resetWinners,
          totalDraws,
          timestamp: Date.now()
        });
      }
    }
  };

  // Toggle Home Page Display Mode (ADMIN ONLY)
  const handleToggleHomeMode = (mode) => {
    const updated = { ...settings, homeDisplayMode: mode };
    setSettings(updated);
    saveSettings(updated);
    playTickSound(880, 0.04);
  };

  const isHomeLivePotOnly = settings?.homeDisplayMode === 'live_pot_draw';

  return (
    <div className={`relative ${isFullscreen ? 'fixed inset-0 z-[200] bg-slate-100 dark:bg-slate-950 p-6 overflow-y-auto text-slate-900 dark:text-white' : 'space-y-6 text-slate-800 dark:text-slate-100'}`}>
      
      {/* Confetti during winner reveal */}
      {drawStatus === 'revealed' && <ConfettiEffect />}

      {/* HEADER BAR */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 rounded-3xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 shadow-sm backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-3xl shrink-0 shadow-inner">
            🏺
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[10px] font-black uppercase tracking-wider">
                Official Stage Raffle
              </span>
              <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">
                {totalDraws === 1 
                  ? "Single Grand Champion Draw" 
                  : totalDraws === 2 
                  ? "Reverse Order: 2nd ➔ 1st" 
                  : "Reverse Order: 3rd ➔ 2nd ➔ 1st"}
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black tracking-tight mt-0.5 text-slate-900 dark:text-white">
              Transparent Glass Pot Token Draw
            </h2>
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <button
                type="button"
                onClick={handleRefreshPool}
                disabled={isRefreshing}
                title="Click to check for newly entered participants now"
                className="px-2.5 py-0.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold flex items-center gap-1.5 hover:bg-emerald-500/25 transition-all cursor-pointer shadow-sm"
              >
                <SafeIcon icon={FiRefreshCw} className={isRefreshing ? "animate-spin text-xs" : "text-xs"} />
                <span>Event Game Pool: <strong>{eligibleEventParticipants.length}</strong></span>
                <span className="text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded font-black">Check</span>
              </button>
              {excludedAnnouncedWinnersCount > 0 && (
                <span className="px-2 py-0.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-[10px] font-bold flex items-center gap-1">
                  <SafeIcon icon={FiAward} />
                  <span>Announced Winners Excluded: <strong>{excludedAnnouncedWinnersCount}</strong></span>
                </span>
              )}
              <span className="px-2 py-0.5 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-700 dark:text-blue-300 text-[10px] font-bold flex items-center gap-1">
                <span>🛡️</span>
                <span>School Games: <strong>Excluded</strong></span>
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Admin Home Page Display Mode Quick Switcher */}
          {isAdmin && (
            <div className="flex items-center gap-1.5 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-xs">
              <span className="text-[10px] font-bold px-2 text-slate-500 uppercase">Home Mode:</span>
              <button
                type="button"
                onClick={() => handleToggleHomeMode('qr_participate')}
                className={`px-2.5 py-1 rounded-lg font-bold text-[11px] transition-all cursor-pointer ${
                  settings?.homeDisplayMode === 'qr_participate'
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                }`}
              >
                QR Code
              </button>
              <button
                type="button"
                onClick={() => handleToggleHomeMode('live_pot_draw')}
                className={`px-2.5 py-1 rounded-lg font-black text-[11px] transition-all cursor-pointer ${
                  settings?.homeDisplayMode === 'live_pot_draw'
                    ? 'bg-amber-500 text-slate-950 shadow-sm'
                    : 'text-amber-600 dark:text-amber-400 hover:underline'
                }`}
                title="Hides QR Code and displays ONLY the transparent glass pot on the home page"
              >
                🏺 Only Glass Pot
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-bold text-xs flex items-center gap-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 shadow-sm cursor-pointer transition-colors"
          >
            <SafeIcon icon={isFullscreen ? FiMinimize2 : FiMaximize2} />
            <span>{isFullscreen ? "Exit Fullscreen" : "Projector View"}</span>
          </button>

          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:hover:text-white transition-colors cursor-pointer"
            >
              <SafeIcon icon={FiX} />
            </button>
          )}
        </div>
      </div>

      {/* STEPPER BAR */}
      <div className={`grid gap-3 text-center ${totalDraws === 1 ? 'grid-cols-1' : totalDraws === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
        {/* Step 1: 3rd Place (Shown for 3-Draws mode) */}
        {totalDraws === 3 && (
          <div className={`p-3.5 rounded-2xl border transition-all ${
            currentStep === 3
              ? 'bg-amber-500/15 border-amber-500 shadow-sm ring-2 ring-amber-500/20'
              : drawnWinners.third
              ? 'bg-emerald-500/10 border-emerald-500/30'
              : 'bg-white dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 opacity-60'
          }`}>
            <div className="text-xl">🥉</div>
            <div className="text-xs font-black text-amber-700 dark:text-amber-400 mt-0.5 uppercase tracking-wider">
              Step 1: 3rd Place
            </div>
            <div className="text-[11px] font-bold text-slate-800 dark:text-slate-200 truncate mt-0.5">
              {drawnWinners.third ? `Won: ${drawnWinners.third.token}` : 'Bronze Trophy'}
            </div>
          </div>
        )}

        {/* Step: 2nd Place (Shown for 2-Draws and 3-Draws mode) */}
        {(totalDraws === 3 || totalDraws === 2) && (
          <div className={`p-3.5 rounded-2xl border transition-all ${
            currentStep === 2
              ? 'bg-slate-300/30 dark:bg-slate-700/40 border-slate-400 shadow-sm ring-2 ring-slate-400/20'
              : drawnWinners.second
              ? 'bg-emerald-500/10 border-emerald-500/30'
              : 'bg-white dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 opacity-60'
          }`}>
            <div className="text-xl">🥈</div>
            <div className="text-xs font-black text-slate-700 dark:text-slate-300 mt-0.5 uppercase tracking-wider">
              {totalDraws === 2 ? 'Step 1: 2nd Place' : 'Step 2: 2nd Place'}
            </div>
            <div className="text-[11px] font-bold text-slate-800 dark:text-slate-200 truncate mt-0.5">
              {drawnWinners.second ? `Won: ${drawnWinners.second.token}` : 'Silver Trophy'}
            </div>
          </div>
        )}

        {/* Step: 1st Place Grand Bumper */}
        <div className={`p-3.5 rounded-2xl border transition-all ${
          currentStep === 1
            ? 'bg-amber-500/20 border-amber-500 shadow-md ring-2 ring-amber-400/40 animate-pulse'
            : drawnWinners.first
            ? 'bg-emerald-500/10 border-emerald-500/30'
            : 'bg-white dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 opacity-60'
        }`}>
          <div className="text-xl">🥇</div>
          <div className="text-xs font-black text-amber-600 dark:text-amber-400 mt-0.5 uppercase tracking-wider">
            {totalDraws === 1 ? 'Grand Draw: 1st Place' : totalDraws === 2 ? 'Step 2: 1st Place Grand' : 'Step 3: 1st Place Grand'}
          </div>
          <div className="text-[11px] font-bold text-slate-800 dark:text-slate-200 truncate mt-0.5">
            {drawnWinners.first ? `Won: ${drawnWinners.first.token}` : 'Mega Bumper Prize'}
          </div>
        </div>
      </div>

      {/* MAIN ARENA: TRANSPARENT GLASS POT DISPLAY */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
        
        {/* CENTER / POT VISUALIZATION */}
        <div className="lg:col-span-7 flex flex-col items-center justify-center p-6 sm:p-8 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl relative overflow-hidden min-h-[460px]">
          
          {/* Ambient Lighting & Glass Reflections */}
          <div className={`absolute inset-0 transition-opacity duration-1000 pointer-events-none ${
            potAgitating 
              ? 'bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-amber-500/20 via-purple-500/10 to-transparent' 
              : 'bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent'
          }`} />

          {/* Holographic Top Suction Funnel */}
          <div className="relative z-20 flex flex-col items-center mb-2">
            <div className={`w-28 h-6 rounded-t-full border-t-2 border-x-2 transition-all ${
              potAgitating ? 'border-amber-400 bg-amber-500/20 shadow-[0_0_20px_rgba(245,158,11,0.6)]' : 'border-slate-300 dark:border-cyan-400/50 bg-slate-100 dark:bg-cyan-500/10'
            }`} />
            <div className="w-16 h-3 bg-gradient-to-b from-slate-200/50 dark:from-white/30 to-transparent border-x border-slate-300 dark:border-white/20" />
          </div>

          {/* THE TRANSPARENT GLASS BOWL */}
          <div className="relative w-72 sm:w-84 md:w-96 h-72 sm:h-84 md:h-96 rounded-full border-4 border-slate-200/80 dark:border-white/20 shadow-[0_20px_60px_rgba(0,0,0,0.15),inset_0_10px_40px_rgba(255,255,255,0.4)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.8),inset_0_10px_40px_rgba(255,255,255,0.15)] backdrop-blur-md overflow-hidden flex items-center justify-center z-10 bg-slate-50/60 dark:bg-slate-950/40">
            
            {/* Glossy Front Glass Reflections */}
            <div className="absolute -top-12 left-6 right-6 h-36 bg-gradient-to-b from-white/60 via-white/15 to-transparent dark:from-white/25 dark:via-white/5 rounded-full pointer-events-none z-30" />
            <div className="absolute bottom-4 left-10 right-10 h-16 bg-gradient-to-t from-white/30 to-transparent dark:from-white/10 rounded-full pointer-events-none z-30" />
            
            {/* Curved Highlights */}
            <div className="absolute inset-2 rounded-full border border-white/40 dark:border-white/10 pointer-events-none z-30" />

            {/* Suction Beam */}
            {potAgitating && (
              <div className="absolute top-0 w-12 h-full bg-gradient-to-b from-amber-400/50 via-amber-300/20 to-transparent animate-pulse pointer-events-none z-20 blur-sm" />
            )}

            {/* FLOATING BOUNCING TOKEN BALLS INSIDE GLASS */}
            <div className="absolute inset-0 p-4">
              {balls.map((ball) => (
                <div
                  key={ball.id}
                  style={{
                    position: 'absolute',
                    left: `${ball.x}%`,
                    top: `${ball.y}%`,
                    transform: `translate(-50%, -50%) rotate(${ball.rotation}deg)`,
                    width: `${ball.size}px`,
                    height: `${ball.size}px`,
                    transition: 'transform 0.05s linear'
                  }}
                  className={`rounded-full bg-gradient-to-br ${ball.color} shadow-md border border-white/60 flex items-center justify-center text-white font-mono font-black select-none pointer-events-none`}
                >
                  <span className="text-[8px] sm:text-[9px] drop-shadow-md text-center leading-none px-0.5 font-bold truncate">
                    {ball.token.replace('TKN-', '#')}
                  </span>
                </div>
              ))}
            </div>

            {/* Center Agitation Vortex */}
            {potAgitating && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                <div className="w-48 h-48 rounded-full border-2 border-dashed border-amber-400 animate-spin" style={{ animationDuration: '0.8s' }} />
                <div className="w-28 h-28 rounded-full border border-amber-300 animate-ping opacity-60" />
              </div>
            )}
          </div>

          {/* Stand */}
          <div className="relative z-10 -mt-3 flex flex-col items-center">
            <div className="w-40 sm:w-48 h-4 bg-gradient-to-r from-slate-300 via-slate-100 to-slate-300 dark:from-slate-700 dark:via-slate-300 dark:to-slate-700 rounded-t-xl shadow-md border-t border-white" />
            <div className="w-56 sm:w-64 h-6 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-800 dark:via-slate-600 dark:to-slate-800 rounded-b-2xl shadow-xl flex items-center justify-center">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-700 dark:text-slate-300">
                Official Raffle Pot
              </span>
            </div>
          </div>

          <p className="text-xs text-slate-500 dark:text-slate-400 mt-4 text-center font-medium">
            {potAgitating 
              ? "⚡ Vortex spinning! Extracting lucky token..." 
              : "🏺 Transparent container loaded with player tokens from live game participation."}
          </p>
        </div>

        {/* RIGHT: CONTROLS & REVEALED WINNERS */}
        <div className="lg:col-span-5 space-y-5">
          
          {/* Admin Control Box OR Public Live Status */}
          <div className="glass-panel p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <div>
              <span className="text-[10px] font-black uppercase text-amber-600 dark:text-amber-400 tracking-wider block">
                {isAdmin ? "Admin Draw Controls" : "Official Stage Broadcast"}
              </span>
              <h3 className="text-lg font-black text-slate-900 dark:text-white mt-0.5">
                {currentStep === 3 && "Step 1: Draw 3rd Place"}
                {currentStep === 2 && (totalDraws === 2 ? "Step 1: Draw 2nd Place" : "Step 2: Draw 2nd Place")}
                {currentStep === 1 && (totalDraws === 1 ? "Grand Champion Draw: 1st Place" : totalDraws === 2 ? "Step 2: Draw 1st Place Grand Champion!" : "Step 3: Draw 1st Place Grand Champion!")}
                {currentStep === 'completed' && "🎉 Grand Draw Complete!"}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {isAdmin ? (
                  currentStep === 3 ? "Click below to execute the 3rd place draw from the glass pot." :
                  currentStep === 2 ? (totalDraws === 2 ? "Click below to execute the 2nd place draw." : "3rd place awarded! Click below to execute the 2nd place draw.") :
                  currentStep === 1 ? "Click below to draw the Grand Champion Bumper Winner!" :
                  "All podium winners have been officially announced."
                ) : (
                  currentStep === 'completed' 
                    ? "Ceremony concluded! Official podium winners revealed."
                    : "The ceremonial draw is controlled live from the stage. Watch the transparent pot above as winning tokens are extracted!"
                )}
              </p>
            </div>

            {/* CEREMONY FORMAT SELECTOR (ADMIN ONLY) */}
            {isAdmin && (
              <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                    <SafeIcon icon={FiSliders} className="text-amber-500" />
                    <span>Ceremony Format:</span>
                  </span>
                  <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400">
                    {totalDraws === 1 ? '1 Winner (Grand)' : totalDraws === 2 ? '2 Winners (Silver & Grand)' : '3 Winners (Full Podium)'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  <button
                    type="button"
                    disabled={drawStatus === 'drawing'}
                    onClick={() => handleSelectTotalDraws(1)}
                    className={`py-1.5 px-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex flex-col items-center ${
                      totalDraws === 1
                        ? 'bg-amber-500 text-slate-950 font-black shadow-sm ring-1 ring-amber-400'
                        : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
                    }`}
                  >
                    <span>🥇 1 Draw</span>
                    <span className="text-[9px] opacity-80 font-normal">Grand Winner</span>
                  </button>
                  <button
                    type="button"
                    disabled={drawStatus === 'drawing'}
                    onClick={() => handleSelectTotalDraws(2)}
                    className={`py-1.5 px-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex flex-col items-center ${
                      totalDraws === 2
                        ? 'bg-amber-500 text-slate-950 font-black shadow-sm ring-1 ring-amber-400'
                        : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
                    }`}
                  >
                    <span>🥈 2 Draws</span>
                    <span className="text-[9px] opacity-80 font-normal">2nd & 1st</span>
                  </button>
                  <button
                    type="button"
                    disabled={drawStatus === 'drawing'}
                    onClick={() => handleSelectTotalDraws(3)}
                    className={`py-1.5 px-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex flex-col items-center ${
                      totalDraws === 3
                        ? 'bg-amber-500 text-slate-950 font-black shadow-sm ring-1 ring-amber-400'
                        : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
                    }`}
                  >
                    <span>🥉 3 Draws</span>
                    <span className="text-[9px] opacity-80 font-normal">Full Podium</span>
                  </button>
                </div>

                {/* Exclude / Include Past Winners Toggle */}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-200 dark:border-slate-700/60">
                  <button
                    type="button"
                    onClick={() => setExcludeAnnouncedWinners(!excludeAnnouncedWinners)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition-all flex items-center gap-1.5 cursor-pointer ${
                      excludeAnnouncedWinners
                        ? 'bg-amber-500/15 border-amber-500/40 text-amber-700 dark:text-amber-300'
                        : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
                    }`}
                  >
                    <span>{excludeAnnouncedWinners ? '☑️ Excluding Past Winners' : '✅ Including All Event Entries'}</span>
                    <span className="opacity-80">({eligibleEventParticipants.length} available)</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleRefreshPool}
                    disabled={isRefreshing}
                    className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 flex items-center gap-1 cursor-pointer transition-colors"
                  >
                    <SafeIcon icon={FiRefreshCw} className={isRefreshing ? "animate-spin text-xs" : "text-xs"} />
                    <span>Sync</span>
                  </button>
                </div>
              </div>
            )}

            {/* ADMIN DRAW TRIGGER BUTTONS (SHOWN ONLY TO ADMIN) */}
            {isAdmin ? (
              currentStep !== 'completed' ? (
                (() => {
                  const drawnIds = [drawnWinners.third?.id, drawnWinners.second?.id, drawnWinners.first?.id].filter(Boolean);
                  const drawnTokens = [drawnWinners.third?.token, drawnWinners.second?.token, drawnWinners.first?.token].filter(Boolean);
                  const activePoolCount = eligibleEventParticipants.filter(p => {
                    const pToken = p.serialNumber || p.token || p.couponCode;
                    return !drawnIds.includes(p.id) && !drawnTokens.includes(pToken) && (!excludeAnnouncedWinners || !isAnnouncedWinnerEntry(p, officialWinners));
                  }).length;

                  return (
                    <div className="space-y-3">
                      {activePoolCount === 0 && (
                        <div className="p-4 rounded-2xl bg-amber-500/15 border border-amber-500/30 text-amber-900 dark:text-amber-200 text-xs space-y-2.5">
                          <div className="flex items-start gap-2.5">
                            <span className="text-xl">⚠️</span>
                            <div className="flex-1">
                              <div className="font-black text-amber-700 dark:text-amber-400">
                                No Eligible Event Game Participants Remaining
                              </div>
                              <p className="text-[11px] mt-0.5 text-slate-600 dark:text-slate-300">
                                All event participants have either already been drawn or are already recorded in the <strong>Announced Winners Directory</strong>. Participants from School Games are strictly excluded from this draw.
                              </p>
                            </div>
                          </div>

                          {/* RESUME & REFRESH ACTION BAR */}
                          <div className="pt-2 border-t border-amber-500/20 flex flex-wrap items-center gap-2">
                            {excludeAnnouncedWinners && allEventParticipants.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setExcludeAnnouncedWinners(false)}
                                className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                              >
                                <span>🔓 Include All ({allEventParticipants.length}) Event Entries</span>
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={handleRefreshPool}
                              disabled={isRefreshing}
                              className="px-3 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-black text-xs flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                            >
                              <SafeIcon icon={FiRefreshCw} className={isRefreshing ? "animate-spin text-xs" : "text-xs"} />
                              <span>{isRefreshing ? "Checking..." : "🔄 Check for New Participants Now"}</span>
                            </button>

                            {currentStep !== 1 && (
                              <button
                                type="button"
                                onClick={() => handleSkipToStep(1)}
                                className="px-2.5 py-1.5 rounded-xl bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-300 dark:border-slate-600 text-xs font-bold hover:bg-slate-100 transition-colors cursor-pointer flex items-center gap-1"
                                title="Skip directly to the 1st Place Grand Champion draw"
                              >
                                <SafeIcon icon={FiFastForward} />
                                <span>Skip to 1st Place</span>
                              </button>
                            )}

                            {(drawnWinners.third || drawnWinners.second) && (
                              <button
                                type="button"
                                onClick={handleConcludeEarly}
                                className="px-2.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-colors cursor-pointer flex items-center gap-1 shadow-sm"
                              >
                                <SafeIcon icon={FiCheck} />
                                <span>Conclude Ceremony Early</span>
                              </button>
                            )}
                          </div>

                          {refreshFeedback && (
                            <div className="text-[11px] text-emerald-700 dark:text-emerald-300 font-bold bg-emerald-500/10 px-2.5 py-1 rounded-lg border border-emerald-500/30">
                              ✓ {refreshFeedback}
                            </div>
                          )}
                        </div>
                      )}

                      <button
                        type="button"
                        disabled={drawStatus === 'drawing' || activePoolCount === 0}
                        onClick={() => handleInitiateDraw(currentStep)}
                        className={`w-full py-4 rounded-2xl font-black text-sm tracking-wide shadow-xl transition-all flex items-center justify-center gap-2 cursor-pointer ${
                          drawStatus === 'drawing'
                            ? 'bg-slate-400 text-white cursor-not-allowed animate-pulse'
                            : activePoolCount === 0
                            ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border border-slate-300 dark:border-slate-700 cursor-not-allowed'
                            : currentStep === 1
                            ? 'bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 hover:from-amber-600 hover:to-yellow-500 text-slate-950 shadow-amber-500/25 ring-2 ring-amber-400/50 hover:scale-[1.02] active:scale-[0.98]'
                            : currentStep === 2
                            ? 'bg-gradient-to-r from-slate-400 via-slate-200 to-slate-400 hover:from-slate-500 hover:to-slate-300 text-slate-950 shadow-slate-400/20 hover:scale-[1.02] active:scale-[0.98]'
                            : 'bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 hover:from-amber-700 hover:to-amber-500 text-white shadow-amber-600/25 hover:scale-[1.02] active:scale-[0.98]'
                        }`}
                      >
                        <SafeIcon icon={drawStatus === 'drawing' ? FiRefreshCw : FiStar} className={drawStatus === 'drawing' ? "animate-spin text-base" : "text-base"} />
                        <span>
                          {drawStatus === 'drawing'
                            ? "Spinning Glass Pot & Drawing..."
                            : activePoolCount === 0
                            ? "⚠️ Awaiting Event Game Participants"
                            : currentStep === 3
                            ? `🥉 Draw 3rd Place Token (${activePoolCount} in pool)`
                            : currentStep === 2
                            ? `🥈 Draw 2nd Place Token (${activePoolCount} in pool)`
                            : `🌟 Draw 1st Place Grand Champion! (${activePoolCount} in pool)`}
                        </span>
                      </button>

                      {/* Helper hint when waiting for participants */}
                      {activePoolCount === 0 && (
                        <p className="text-[11px] text-center text-slate-500 dark:text-slate-400">
                          💡 Once another visitor plays an event game, this button automatically unlocks. You can also switch to <strong>1 Draw</strong> or <strong>2 Draws</strong> above.
                        </p>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-300 dark:border-emerald-800 text-center space-y-2">
                  <span className="text-2xl">🏆</span>
                  <div className="text-xs font-black text-emerald-700 dark:text-emerald-300">
                    {totalDraws === 1 
                      ? "Grand Champion Drawn Successfully!" 
                      : totalDraws === 2 
                      ? "Top 2 Winners Drawn Successfully!" 
                      : "All 3 Podium Winners Drawn Successfully!"}
                  </div>
                  <button
                    type="button"
                    onClick={handleResetDrawSession}
                    className="px-4 py-2 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold text-xs shadow-md transition-all cursor-pointer"
                  >
                    Start New Draw Round
                  </button>
                </div>
              )
            ) : (
              /* NON-ADMIN / PUBLIC HOME PAGE DISPLAY (NO DRAW BUTTONS) */
              <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 flex items-center gap-3">
                <span className="text-2xl">📡</span>
                <div>
                  <div className="text-xs font-bold text-slate-800 dark:text-slate-200">
                    {drawStatus === 'drawing' ? 'Extracting Winning Token Right Now...' : 'Live Stage Broadcast Active'}
                  </div>
                  <div className="text-[10px] text-slate-500 dark:text-slate-400">
                    Tokens spin dynamically inside the transparent pot during live draw.
                  </div>
                </div>
              </div>
            )}

            {/* Bottom Actions: Early Conclude & Reset */}
            {isAdmin && currentStep !== 'completed' && (drawnWinners.third || drawnWinners.second || drawnWinners.first) && (
              <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-800 text-[11px]">
                <button
                  type="button"
                  onClick={handleConcludeEarly}
                  className="font-bold text-emerald-600 dark:text-emerald-400 hover:underline cursor-pointer flex items-center gap-1"
                >
                  <SafeIcon icon={FiCheck} />
                  <span>Conclude Ceremony Now</span>
                </button>
                <button
                  type="button"
                  onClick={handleResetDrawSession}
                  className="font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors cursor-pointer"
                >
                  Reset Round
                </button>
              </div>
            )}
          </div>

          {/* LATEST DRAWN WINNER DISPLAY CARD */}
          <AnimatePresence mode="wait">
            {currentWinner && (
              <motion.div
                key={currentWinner.token}
                initial={{ opacity: 0, y: 20, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                className={`p-5 rounded-3xl border shadow-lg relative overflow-hidden ${
                  currentWinner.place === 1
                    ? 'bg-amber-500/10 border-amber-500/50'
                    : currentWinner.place === 2
                    ? 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600'
                    : 'bg-amber-700/10 border-amber-700/40'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-black text-xl shadow-md ${
                      currentWinner.place === 1
                        ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-slate-950 ring-2 ring-amber-300'
                        : currentWinner.place === 2
                        ? 'bg-gradient-to-br from-slate-200 to-slate-400 text-slate-900'
                        : 'bg-gradient-to-br from-amber-700 to-amber-900 text-amber-100'
                    }`}>
                      #{currentWinner.place}
                    </div>
                    <div>
                      <span className="text-[10px] font-black uppercase tracking-wider block text-amber-600 dark:text-amber-400">
                        {currentWinner.placeLabel}
                      </span>
                      <h4 className="text-base font-black text-slate-900 dark:text-white leading-tight">
                        {currentWinner.name}
                      </h4>
                      <div className="text-[11px] font-mono font-bold text-slate-500 dark:text-slate-400 mt-0.5">
                        Token: <span className="text-indigo-600 dark:text-indigo-400 font-black">{currentWinner.token}</span>
                      </div>
                    </div>
                  </div>

                  <span className="px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-black text-[10px] uppercase">
                    ✓ Confirmed
                  </span>
                </div>

                <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-xs">
                  <span className="text-slate-500 font-medium">Awarded Prize:</span>
                  <span className="font-black text-amber-600 dark:text-amber-400">{currentWinner.reward}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* OFFICIAL PODIUM BOARD */}
          <div className="glass-panel p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">
                Ceremonial Podium Status:
              </span>
              <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400">
                {totalDraws === 1 ? '1 Winner Round' : totalDraws === 2 ? '2 Winners Round' : '3-Tier Podium'}
              </span>
            </div>
            <div className="space-y-2">
              {/* 1st Place */}
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-base">🥇</span>
                  <span className="font-bold text-slate-800 dark:text-slate-200">1st Place (Grand):</span>
                </div>
                <div className="font-bold text-amber-500">
                  {drawnWinners.first ? `${drawnWinners.first.name} (${drawnWinners.first.token})` : <span className="text-slate-400 font-normal italic">Waiting for Step {totalDraws === 1 ? '1' : totalDraws === 2 ? '2' : '3'}</span>}
                </div>
              </div>

              {/* 2nd Place */}
              {(totalDraws === 2 || totalDraws === 3 || drawnWinners.second) && (
                <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-base">🥈</span>
                    <span className="font-bold text-slate-800 dark:text-slate-200">2nd Place (Silver):</span>
                  </div>
                  <div className="font-bold text-slate-600 dark:text-slate-300">
                    {drawnWinners.second ? `${drawnWinners.second.name} (${drawnWinners.second.token})` : <span className="text-slate-400 font-normal italic">Waiting for Step {totalDraws === 2 ? '1' : '2'}</span>}
                  </div>
                </div>
              )}

              {/* 3rd Place */}
              {(totalDraws === 3 || drawnWinners.third) && (
                <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-base">🥉</span>
                    <span className="font-bold text-slate-800 dark:text-slate-200">3rd Place (Bronze):</span>
                  </div>
                  <div className="font-bold text-amber-700 dark:text-amber-400">
                    {drawnWinners.third ? `${drawnWinners.third.name} (${drawnWinners.third.token})` : <span className="text-slate-400 font-normal italic">Waiting for Step 1</span>}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

    </div>
  );
};

export default GlassPotTokenDraw;
